import styles from './SignInPage.module.scss';
import commonStyles from './SignInOrOutPageCommon.module.scss';
import ArchonLayout, {
  Background,
  Container,
} from '../../layouts/ArchonLayout/ArchonLayout';
import { signIn, useSession } from 'next-auth/react';
import React, { useEffect } from 'react';
import Seo from '../Seo/Seo';
import Button from '@resources/js/components/Button/Button';
import Heading from '@resources/js/components/Heading/Heading';
import Body from '@resources/js/components/Body/Body';
import useTheme from '@resources/js/contexts/useTheme';
import { SignInPageViewModel } from '../../helpers/getSignInPageViewModels';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import WarningParagraph from '@resources/js/components/WarningParagraph/WarningParagraph';
import cx from 'classnames';

type SignInPageProps = {
  page: SignInPageViewModel;
};

export default function SignInPage({ page }: SignInPageProps) {
  return (
    <ArchonLayout>
      <SignInPageContent page={page} />
    </ArchonLayout>
  );
}

function SignInPageContent({ page }: SignInPageProps) {
  const {
    seo,
    title,
    description,
    label,
    redirectingMessage,
    providers,
    errors,
  } = page;
  const theme = useTheme();
  const { status } = useSession();
  const router = useRouter();
  const error = router.query.error;

  const signInErrors: Record<string | 'default', string> = {
    default: errors.unableToSignIn,
    Signin: errors.trySigningInWithADifferentAccount,
    OAuthSignin: errors.trySigningInWithADifferentAccount,
    OAuthCallbackError: errors.trySigningInWithADifferentAccount,
    OAuthCreateAccount: errors.trySigningInWithADifferentAccount,
    EmailCreateAccount: errors.trySigningInWithADifferentAccount,
    Callback: errors.trySigningInWithADifferentAccount,
    OAuthAccountNotLinked: errors.signInWithTheAccountYouUsedOriginally,
    EmailSignin: errors.emailCouldNotBeSent,
    CredentialsSignin: errors.signInFailedCheckDetails,
    SessionRequired: errors.signInToAccessPage,
  };

  useEffect(() => {
    if (status === 'authenticated') {
      router
        .push(router.query.callbackUrl?.toString() ?? '/')
        .catch(console.error);
    }
  }, [router, status]);

  return (
    <>
      <Seo {...seo} />
      <Background />
      <Container className={commonStyles.container}>
        <Body className={commonStyles.box}>
          <div>
            <Heading level={1}>{title}</Heading>
            {error && typeof error === 'string' ? (
              <WarningParagraph style={'red'}>
                {signInErrors[error] ?? signInErrors['default']}
              </WarningParagraph>
            ) : null}
            <p className={styles.description}>
              {status === 'loading'
                ? ''
                : status === 'authenticated'
                ? redirectingMessage
                : description}
            </p>
          </div>
          <div
            className={cx({
              [commonStyles.labelAndButtons]: true,
              [commonStyles.labelAndButtonsHidden]:
                status !== 'unauthenticated',
            })}
          >
            <span className={styles.label}>{label}</span>
            <div className={styles.buttons}>
              {Object.values(providers).map((provider) => (
                <Button
                  key={provider.id}
                  onClick={() => signIn(provider.id)}
                  styles={theme.secondaryButtonStyles}
                  className={styles.button}
                >
                  <Image
                    src={provider.imageUrl}
                    width={32}
                    height={32}
                    alt={''}
                  />{' '}
                  {provider.label}
                  <span className={styles.buttonBuffer} />
                </Button>
              ))}
            </div>
          </div>
        </Body>
      </Container>
    </>
  );
}
